'use strict';

import Component from 'org/components/core/Component';

const SDK_POLL_INTERVAL = 100;
const SDK_POLL_TIMEOUT = 10000;

/**
 * Wires the PayPal logo to PayPal's own Pay Later modal. PayPal's Pay Later terms do not
 * allow us to author or host the installment wording, so the entire disclosure comes from
 * paypal.MessagesModal. The whole block stays hidden until the SDK has produced a modal,
 * otherwise the logo would be an inert control whenever PayPal is unreachable.
 */
export default class PayPalMessages extends Component {
    init() {
        super.init();

        this.modal = null;
        this.sdkPoll = null;
        this.destroyed = false;
        this.amount = this.readAmount();

        this.whenSdkReady()
            .then(() => this.createModal())
            .catch(() => {});

        this.eventDelegate('click', '.paypal-learnmore', this.onClick.bind(this));
        this.event('ajaxComplete', this.onAjaxComplete.bind(this), $(document));
        this.event('product:afterAttributeSelect', this.onVariantSelect.bind(this), $('body'));
    }

    /**
     * Resolves once the asynchronously loaded PayPal SDK has published its global.
     * @returns {Promise} resolves when window.paypal.MessagesModal is callable
     */
    whenSdkReady() {
        return new Promise((resolve, reject) => {
            if (window.paypal && window.paypal.MessagesModal) {
                resolve();
                return;
            }

            var waited = 0;
            this.sdkPoll = setInterval(() => {
                if (window.paypal && window.paypal.MessagesModal) {
                    this.stopPolling();
                    resolve();
                } else if (waited >= SDK_POLL_TIMEOUT) {
                    this.stopPolling();
                    reject(new Error('PayPal messaging SDK did not load'));
                }
                waited += SDK_POLL_INTERVAL;
            }, SDK_POLL_INTERVAL);
        });
    }

    stopPolling() {
        if (this.sdkPoll) {
            clearInterval(this.sdkPoll);
            this.sdkPoll = null;
        }
    }

    /**
     * Stops the SDK poll so it cannot outlive the element and touch a torn down component.
     */
    destroy() {
        this.destroyed = true;
        this.stopPolling();
        super.destroy();
    }

    createModal() {
        if (this.destroyed) {
            return;
        }

        var options = {};

        if (this.amount !== null) {
            options.amount = this.amount;
        }

        this.modal = window.paypal.MessagesModal(options);
        this.$el.addClass('m-ready');
    }

    /**
     * Reads the server supplied amount off the element.
     * @returns {number|null} the amount, or null when it is not a usable number
     */
    readAmount() {
        var amount = parseFloat(this.$el.attr('data-amount'));
        return Number.isNaN(amount) ? null : amount;
    }

    /**
     * Pushes a new amount into the modal. MessagesModal instances are memoised by PayPal on
     * the account details, so a changed amount has to go through updateProps rather than a
     * fresh instance.
     * @param {number} amount - the transaction amount to advertise
     */
    setAmount(amount) {
        // The document level ajaxComplete handler can still fire against a torn down
        // component, at which point the base class has already dropped this.$el
        if (this.destroyed || typeof amount !== 'number' || Number.isNaN(amount) || amount === this.amount) {
            return;
        }

        this.amount = amount;
        this.$el.attr('data-amount', amount);

        if (this.modal) {
            this.modal.updateProps({ amount: amount });
        }
    }

    onClick(event) {
        event.preventDefault();

        if (!this.modal) {
            return;
        }

        var shown = this.modal.show();
        if (shown && typeof shown.catch === 'function') {
            shown.catch(() => {});
        }
    }

    /**
     * Picks up basket totals recalculated server side. PayPal requires the advertised amount
     * to come from the server rather than being derived from the rendered totals.
     * @param {Object} event - jQuery ajaxComplete event
     * @param {Object} xhr - the completed jQuery XHR object
     */
    onAjaxComplete(event, xhr) {
        var body = xhr && xhr.responseJSON;
        if (!body) {
            return;
        }

        var totals = body.totals || (body.basket && body.basket.totals) || (body.cartModel && body.cartModel.totals);
        if (!totals || totals.payPalAmount == null) {
            return;
        }

        this.setAmount(parseFloat(totals.payPalAmount));
    }

    onVariantSelect(event, response) {
        var salesPrice = response && response.data && response.data.product
            && response.data.product.price && response.data.product.price.sales;

        if (!salesPrice || salesPrice.value == null) {
            return;
        }

        this.setAmount(parseFloat(salesPrice.value));
    }
}
